// StudioCardSlot — one card where it sits on the Studio canvas: its frame
// (every state of pen "Studio – detail design" §3: hover, selected, keyboard
// focus, dragging, a drop target, boxed, just revealed), its body (a
// container's ModuleCard, else a StudioCard) and its `…` menu. Pure: the
// canvas decides every state and owns every handler.
//
// Accessible name: "{kind}, {title}, {state}" (§7); being selected is the
// button's pressed state.

export { StudioCardSlot };

import { useLingui } from "@lingui/react/macro";
import type { KeyboardEvent, MouseEvent, PointerEvent, ReactNode } from "react";
import type { MenuItem } from "../Menu/Menu.js";
import { ModuleCard } from "../ModuleCard/ModuleCard.js";
import type { PartIntent } from "../ModuleCard/partOps.js";
import { RowOverflow } from "../RowOverflow/RowOverflow.js";
import { StudioCard } from "../StudioCard/StudioCard.js";
import type { StudioCard as Card } from "./studioCards.js";
import type { Point } from "./studioLayout.js";
import type { FindLook } from "./useStudioFind.js";

type SlotState = {
    readonly at: Point;
    readonly width: number | undefined;
    readonly selected: boolean;
    readonly boxed: boolean;
    readonly dropTarget: boolean;
    readonly dragging: boolean;
    // The number a dragged card would get in this container.
    readonly dropSlot: number | undefined;
    readonly folded: boolean;
    // Lifted open on the desk: only its outline stays on its spot.
    readonly lifted: boolean;
    readonly naming: boolean;
    readonly online: boolean;
    // Set: highlighted as just revealed (a new value restarts the highlight).
    readonly revealed: number | undefined;
    // While something is looked for on the desk: lit or faded.
    readonly found: FindLook | undefined;
};

type SlotHandlers = {
    readonly onPointerDown: (e: PointerEvent<HTMLDivElement>) => void;
    readonly onDoubleClick: (e: MouseEvent<HTMLDivElement>) => void;
    readonly onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void;
    readonly onPart: (intent: PartIntent) => void;
    readonly onNamed: (title: string | null) => void;
    readonly onToggleFold: () => void;
};

const StudioCardSlot = ({
    card,
    state,
    menu,
    handlers,
}: {
    readonly card: Card;
    readonly state: SlotState;
    readonly menu: readonly MenuItem[];
    readonly handlers: SlotHandlers;
}): ReactNode => {
    const { t } = useLingui();
    const kind = ((): string => {
        switch (card.type) {
            case "source":
                return t`Source`;
            case "material":
                return card.template === "quiz"
                    ? t`Quiz`
                    : card.template === "question"
                      ? t`Question`
                      : t`Material`;
            case "container":
                return card.kind === "course" ? t`Course` : t`Module`;
            case "generic":
                return t`Item`;
        }
    })();
    const states = [
        ...(state.folded ? [t`folded`] : []),
        ...(card.type === "material" && card.planned ? [t`queued`] : []),
        ...(card.type === "container" &&
        card.conformance.type === "not_conforming"
            ? [t`not conforming`]
            : []),
    ];
    return (
        <div
            data-card-id={card.id}
            className="bilda-studio__slot bilda-row-overflow-host"
            data-dragging={state.dragging ? "true" : undefined}
            data-lifted={state.lifted ? "true" : undefined}
            data-found={state.found?.type}
            style={{ left: state.at.x, top: state.at.y, width: state.width }}
        >
            {/* biome-ignore lint/a11y/useSemanticElements: the card holds block content (a title paragraph, a parts list), which a <button> may not; role="button" + tabIndex + key handling make it one. */}
            <div
                role="button"
                tabIndex={0}
                aria-pressed={state.selected}
                aria-label={[kind, card.title, ...states].join(", ")}
                className="bilda-studio__card"
                data-kind={card.type === "container" ? card.kind : card.type}
                data-folded={state.folded ? "true" : undefined}
                data-selected={state.selected ? "true" : undefined}
                data-boxed={state.boxed ? "true" : undefined}
                data-drop-target={state.dropTarget ? "true" : undefined}
                onPointerDown={handlers.onPointerDown}
                onDoubleClick={handlers.onDoubleClick}
                onKeyDown={handlers.onKeyDown}
            >
                {card.type === "container" ? (
                    <ModuleCard
                        card={card}
                        online={state.online}
                        onPart={handlers.onPart}
                        naming={state.naming}
                        onNamed={handlers.onNamed}
                        dropSlot={state.dropSlot}
                        folded={state.folded}
                        onToggleFold={handlers.onToggleFold}
                    />
                ) : (
                    <StudioCard card={card} />
                )}
                {state.revealed === undefined ? null : (
                    <span
                        key={state.revealed}
                        className="bilda-studio__reveal"
                        aria-hidden="true"
                    />
                )}
            </div>
            {/* After the card, so it is drawn over its edge. */}
            {state.found?.type === "lit" && state.found.inside > 0 ? (
                <span className="bilda-studio__found-inside">
                    {t`${state.found.inside} inside`}
                </span>
            ) : null}
            <RowOverflow
                className="bilda-studio__card-menu"
                label={t`Actions for ${card.title}`}
                items={menu}
            />
        </div>
    );
};
